import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { observer } from 'mobx-react';
import React, { FC, useState } from 'react';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { useBidderChangesLogs } from '../../../modules/hooks/apis/use-bidder-changes-logs';
import { useBidderChangesLogsHeaders } from '../../../modules/hooks/theaders/bidder-changes-logs.headers';
import { sortData } from '../../../modules/hooks/tools/use-merge-with-performance';
import { BidderChangesDater } from '../../../modules/components/bidder-changes-dater/bidder-changes-dater';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { IDateSelection } from '@monorepo/controlled/src/stores/date.store';
import dayjs from 'dayjs';
import styles from './bidder-changes-logs.module.scss';
import { useDownload } from '@monorepo/tools/src/lib/hooks/tools/use-download';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import { BidderApi } from '../../../modules/apis/bidder.api';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { useBidderChangesLogsActions } from '../../../modules/hooks/actions/bidderChangesLogs.actions';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'bidder-changes-logs')] };

export const BidderChangesLogs: FC = observer(() => {
	const [currentDateSelection, setCurrentDateSelection] = useState<IDateSelection | null>(null);
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { columns } = useBidderChangesLogsHeaders();
	const { settingsStore } = useStores<IAdminxStore>();
	const { toastsStore } = useStores<IAdminxStore>();
	const sortBy = usePageSettings().tableStore?.getSortingBy();
	const [isLoadingCsv, setIsLoadingCsv] = useState<boolean>(false);
	const { FiltersActions } = useBidderChangesLogsActions({
		currentRoute: currentRouteWithoutSpecialChars,
	});

	const handleDateChange = (dateSelection: IDateSelection) => {
		setCurrentDateSelection(dateSelection);
	};

	const startDate = currentDateSelection?.startDate ? dayjs(currentDateSelection.startDate).format('YYYY-MM-DDTHH:mm') : '';
	const endDate = currentDateSelection?.endDate ? dayjs(currentDateSelection.endDate).format('YYYY-MM-DDTHH:mm') : '';

	const { data, isLoading, error } = useBidderChangesLogs({
		startDate: currentDateSelection && startDate ? startDate : undefined,
		endDate: currentDateSelection && endDate ? endDate : undefined,
	});

	const sortedList = sortData(data, sortBy?.[0]);

	const filterStore = settingsStore.getPages().get('bidderchangeslogs')?.tableStore.getFiltersStore('bidderchangeslogs');
	const formatDateTime = (dateTime: string) => {
		if (!dateTime) {
			return dateTime;
		}
		return dateTime.replace('T', ' ') + ':00';
	};

	const onDownloadLogsCsv = () => {
		const fileName = `Bidder Changes Logs ${dayjs(currentDateSelection?.startDate).format('YYYY/MM/DD')} - ${dayjs(currentDateSelection?.endDate).format('YYYY/MM/DD')}`;
		if (!data || data.length === 0) {
			toastsStore.addToast({
				msg: 'Cannot export empty file.',
				type: DispatchLogsTypes.Info,
			});
			return;
		}
		setIsLoadingCsv(true);

		const formattedStartDate = formatDateTime(startDate);
		const formattedEndDate = formatDateTime(endDate);

		// Extract campaign ID from active filters
		let downloadCampaignId = 0;
		const currentFilters = filterStore?.getCurrentFilters();
		if (currentFilters) {
			for (const [, filter] of currentFilters) {
				if (filter.prototype.prop === 'bidderchangeslogscampaign_id' && filter.value) {
					downloadCampaignId = Number(filter.value);
					break;
				}
			}
		}

		useDownload({
			httpStore: new HttpStore<undefined, File | null>({
				httpFunc: () =>
					BidderApi.downloadApiChangesLogData(formattedStartDate, formattedEndDate, downloadCampaignId).finally(() =>
						setIsLoadingCsv(false)
					),
			}),
			fileName,
			extension: 'csv',
		});
	};

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Bidder Api Changes Logs</PagebarTitle>
				<PagebarActions>
					<BidderChangesDater onDateChange={handleDateChange} />
				</PagebarActions>
			</Pagebar>

			{error && <div className={styles.errorMessage}>{`Error: ${error.data}`}</div>}

			{!currentDateSelection && <div className={styles.noDataMessage}>Loading default date range...</div>}

			{currentDateSelection && data && data.length > 0 && (
				<ControlledTable
					currentRoute={currentRouteWithoutSpecialChars}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'bidder-changes-logs-table')] }}
					columns={columns}
					data={sortedList}
					onExportToCsv={onDownloadLogsCsv}
					isLoadingCsv={isLoadingCsv}
					defaultSortBy={[{ id: 'ts', desc: true }]}
					isLoading={isLoading}>
					{() => {
						return {
							SelectedActions: () => <div></div>,
							FiltersActions,
						};
					}}
				</ControlledTable>
			)}

			{currentDateSelection && !isLoading && data && data.length === 0 && (
				<div className={styles.noDataMessage}>No logs found for the specified date range.</div>
			)}
		</Page>
	);
});
